import { Filter, Texture, UniformGroup } from 'pixi.js';
import { quadProgram } from '../filterQuad';
import { warnIfDeadSource } from '../shaderTrace';
import { BASE, LIGHT, rgb01 } from '../palette';
import type { DisturbancePack } from './field';
import { MAX_DISTURBANCES } from './field';
import { HALFTONE_FRAGMENT, SCALAR_FIELD_FRAGMENT } from './shaders';

/**
 * 两个阶段的 Pixi 包装。
 *
 * ⚠️ 每个 filter 的参数都放在**具名 resource 组**里（`fieldUniforms` /
 * `halftoneUniforms`），不散在顶层 —— 这样调参时能一眼看出哪个 uniform
 * 属于哪一级，也不会和别的 filter 撞名。
 *
 * ⚠️ 两个都用 `render/filterQuad.ts` 那个顶点着色器（而不是 Pixi 默认的那个）。
 * 差别只有一个 varying：`vQuadCoord` = 滤镜四边形自己的 [0,1] 坐标。
 * 为什么必须要它，见那个文件的说明 —— 简单说：**算位置不能用
 * `vTextureCoord`**，它在纹理池给的贴图上不是 [0,1]。
 */

// ============================================================
// 阶段一：标量场 → 灰阶
// ============================================================

export interface ScalarFieldUniformValues {
  time: number;
  resolution: readonly [number, number];
  /**
   * 滤镜四边形在**画布逻辑坐标**里的矩形 `(x, y, w, h)`。
   *
   * 默认 `(0, 0, w, h)` —— 也就是"四边形就是画布"，和旧行为一致。
   * ⚠️ 背景是**唯一**会把它设成别的东西的地方（窗口不是 16:10 时，
   * 四边形要盖住整块屏幕）。见 `shaders.ts` 文件头"关于坐标系"
   */
  quad: readonly [number, number, number, number];
  /** 卡牌占地遮罩的强度。0 = 不挖洞（调试用） */
  maskStrength: number;
  /** 濒死裂缝遮罩的强度。0 = 不画裂缝（调试用） */
  crackStrength: number;
  /** 初始状态（alexharri 那层）的参数，见 `shaders.ts` 的 uniform 说明 */
  baseAmp: number;
  baseBias: number;
  baseScale: number;
  baseSpeed: number;
  /** gain / gamma / contrast / bias */
  tone: readonly [number, number, number, number];
  /** lo / hi */
  window: readonly [number, number];
}

/**
 * 标量场 filter。
 *
 * ⚠️ 它**忽略自己的输入纹理**（`uTexture`）—— 场是从参数现算出来的，
 * 不是从上面那张图上加工出来的。所以它必须挂在**一个铺满背景的占位精灵**上，
 * 而不是"给某张图加个滤镜"。这也是它和阶段二最大的区别。
 */
export class ScalarFieldFilter extends Filter {
  readonly fieldUniforms: UniformGroup;

  /**
   * @param mask 卡牌占地遮罩。背景会把它采样成"哪里不许有场"
   */
  constructor(mask: Texture, values: Partial<ScalarFieldUniformValues> = {}) {
    const uniforms = new UniformGroup({
      uTime: { value: 0, type: 'f32' },
      uResolution: { value: new Float32Array([1280, 800]), type: 'vec2<f32>' },
      uQuad: { value: new Float32Array([0, 0, 1280, 800]), type: 'vec4<f32>' },
      uMaskStrength: { value: 1, type: 'f32' },
      uCrackStrength: { value: 0, type: 'f32' },
      // 数组 uniform：用 size 而不是写在类型里（Pixi 强制的写法）
      uDistA: { value: new Float32Array(MAX_DISTURBANCES * 4), type: 'vec4<f32>', size: MAX_DISTURBANCES },
      uDistB: { value: new Float32Array(MAX_DISTURBANCES * 4), type: 'vec4<f32>', size: MAX_DISTURBANCES },
      uDistC: { value: new Float32Array(MAX_DISTURBANCES * 4), type: 'vec4<f32>', size: MAX_DISTURBANCES },
      // 初始状态（alexharri 的渐变背景）：1 = 原版、0 = 一整片平的中灰
      uBaseAmp: { value: 1, type: 'f32' },
      uBaseBias: { value: 0, type: 'f32' },
      uBaseScale: { value: 1, type: 'f32' },
      uBaseSpeed: { value: 1, type: 'f32' },
      // ⚠️ 底色是**纯黑**、点是**白**的（acid design）。
      // 灰阶 = 白点的覆盖率：0 = 纯黑、1 = 实心白。
      //
      // 取向是"**大部分黑，少量白**"：底黑得下来，白点才刺得出来。
      // 觉得不够明显要往下压 uWindow.x（降阈值让更多点冒出来），
      // 不要动颜色 —— 颜色是色板的事（见 palette.ts）
      uTone: { value: new Float32Array([1, 0.9, 1.1, 0]), type: 'vec4<f32>' },
      // 窗口宽度决定"对比度"：窄 → 同样的噪声起伏映射出更大的网点变化。
      // ⚠️ fbm 是**去均值**的（见 shaders.ts），所以"什么都没有"真的落在中灰
      //    0.5 —— 窗口才可以围着 0.5 上面那一段来取
      // ⚠️ 这一对数字**必须对着能看见的画面调**，而第一版是屏幕全黑的时候
      //    拍的脑袋。测得的环境覆盖率（无扰动、冻结时间）：
      //
      //    lo=0.46（旧默认，当时噪声还带着直流偏置）→ mean 117/255：
      //                       **大半个屏幕是白的**，和上面写的"大部分黑、
      //                       少量白"正好相反
      //    lo=0.65        → mean 6：太干净，看不出是"印刷纹理"
      //    lo=0.62        → mean 11：黑底上清清楚楚一层白点，扰动还能再往上顶
      //
      //    所以取 0.62。要更张扬就往下压 lo，要更含蓄就往上抬 —— 幅度是
      //    非线性的一档，0.03 就换一档观感
      uWindow: { value: new Float32Array([0.62, 0.85]), type: 'vec2<f32>' },
    });

    super({
      glProgram: quadProgram(SCALAR_FIELD_FRAGMENT, 'bg-scalar-field'),
      resources: {
        fieldUniforms: uniforms,
        // ⚠️ 这里必须传 **`mask.source`（TextureSource）**，不能传 `Texture`。
        //
        //    Pixi 的 WebGL uniform 同步（`GenerateShaderSyncCode`）只认
        //    `TextureSource` 和 `TextureView` 两种资源，而 `Texture`
        //    **哪个都不是**（它直接继承 EventEmitter）。传 Texture 不会报错、
        //    不会警告 —— 这一条会被**静默跳过**：采样器不绑定，`uMask` 于是
        //    读到上一个 draw 留在 0 号纹理单元上的东西。
        //
        //    踩了整整一轮：背景页的场**永远是纯黑**，而场本身、参数、半调
        //    全都是好的。因为最后一行 `gray *= 1 - mask` 读到的恰好是
        //    过滤器自己的输入（一张不透明的白占位图）= 1，整块被乘成 0。
        //    更难查的是它**跟页面有关** —— 别的页面 unit 0 上留的可能是别的
        //    贴图，于是"同一个背景，在这里全黑、在那里正常"。
        //
        //    ⚠️ 代价是丢掉 Texture 的 frame（子区域）。遮罩一律是整张贴图，
        //       所以无所谓；哪天要传子区域，得自己把 frame 传进去。
        uMask: mask.source,
        // ⚠️ 同一个理由：两颗采样器都必须是 `TextureSource`。裂缝那张默认
        //    借卡牌遮罩兜底，强度是 0（等于没接）—— 于是"没有裂缝"和
        //    "裂缝贴图还没送进来"长得一模一样，不会有半张脏图
        uCrack: mask.source,
      },
    });

    this.fieldUniforms = uniforms;
    this.setValues(values);
  }

  /**
   * 改参数（只写给了的那些字段）。
   *
   * ⚠️ 名字刻意**不叫 `apply`** —— `Filter` 基类上有一个同名的管线方法
   * （签名是"把这一级渲染出去"），覆盖它会让滤镜彻底不工作，
   * 而且类型报错信息完全指不到问题本身。
   */
  setValues(values: Partial<ScalarFieldUniformValues>): void {
    const u = this.fieldUniforms.uniforms;
    if (values.time !== undefined) u.uTime = values.time;
    if (values.resolution !== undefined) writeVec(u.uResolution, values.resolution);
    if (values.quad !== undefined) writeVec(u.uQuad, values.quad);
    if (values.maskStrength !== undefined) u.uMaskStrength = values.maskStrength;
    if (values.crackStrength !== undefined) u.uCrackStrength = values.crackStrength;
    if (values.baseAmp !== undefined) u.uBaseAmp = values.baseAmp;
    if (values.baseBias !== undefined) u.uBaseBias = values.baseBias;
    if (values.baseScale !== undefined) u.uBaseScale = values.baseScale;
    if (values.baseSpeed !== undefined) u.uBaseSpeed = values.baseSpeed;
    if (values.tone !== undefined) writeVec(u.uTone, values.tone);
    if (values.window !== undefined) writeVec(u.uWindow, values.window);
  }

  /**
   * 换遮罩贴图。
   *
   * ⚠️ 遮罩被销毁重建之后**必须**调它 —— 否则着色器采样的是一张已经
   * 释放掉的贴图，表现是"挖洞突然消失"或者更糟的花屏。
   *
   * ⚠️ 传 `Texture` 进来，但存进去的是 `mask.source`。理由见构造函数里那段。
   *
   * ⚠️ **裂缝那张也跟着换**（只要它还没被真的裂缝接上）：构造时它借的就是
   *    卡牌遮罩兜底（`uCrackStrength = 0`，等于没接）。旧遮罩一销毁，
   *    `uCrack` 就指着一张死贴图 —— 换发光/换遮罩之后下一帧整条滤镜链
   *    直接抛 `Cannot read properties of null (reading 'source')`。
   *    调试页拖"扩散半径"滑块就是这个路径（`CardLab.setGlow`）。
   */
  setMask(mask: Texture): void {
    warnIfDeadSource('ScalarFieldFilter.setMask', mask.source);
    this.resources.uMask = mask.source;
    if (!this.#crackOwn) this.resources.uCrack = mask.source;
  }

  /** 裂缝遮罩是不是**真的**接上过（没有的话就跟着卡牌遮罩走） */
  #crackOwn = false;

  /**
   * 换濒死裂缝遮罩。同一张图里"白 = 这一块场置零"。
   *
   * ⚠️ 和 `setMask` 一样只能收 `TextureSource`。裂缝那一层**每帧都在重画**
   * 同一张 RT，所以正常路径下这张贴图只在建的时候设一次；`setCrack` 是给
   * "整层被销毁重建"（改参数、换倍率）用的。
   */
  setCrack(mask: Texture): void {
    warnIfDeadSource('ScalarFieldFilter.setCrack', mask.source);
    this.resources.uCrack = mask.source;
    this.#crackOwn = true;
  }

  /**
   * 把这一帧的扰动写进去。
   *
   * ⚠️ 这里是**整块复制**（`set`），不是逐条赋值 —— 12 条 × 3 个 vec4
   * 就是 144 个浮点数，逐条写在每帧的循环里是纯粹的浪费。
   */
  setPack(pack: DisturbancePack): void {
    const u = this.fieldUniforms.uniforms;
    (u.uDistA as Float32Array).set(pack.a);
    (u.uDistB as Float32Array).set(pack.b);
    (u.uDistC as Float32Array).set(pack.c);
  }
}

// ============================================================
// 阶段二：灰阶 → 半调
// ============================================================

export interface HalftoneUniformValues {
  resolution: readonly [number, number];
  /** 四边形在画布逻辑坐标里的矩形。默认 = 整块 resolution（= 旧行为） */
  quad: readonly [number, number, number, number];
  /**
   * **这一级往哪张表面上画**，那东西的**像素尺寸**（不是逻辑尺寸）。
   *
   * 像素网格对齐（`shaders.ts` 里那段"先把采样点吸附到像素网格上"）要用它：
   * `一个逻辑单位 = 几个像素` 就是 `quad.zw / targetSize`。
   *
   * ⚠️ 背景会把它设成 FBO 的尺寸（`1/2^n` 屏幕那块），**不是**画布尺寸 ——
   *    这就是"半调网格跟着像素网格走"的全部内容。
   *
   * ⚠️ 不给 = 跟着 `resolution`（也就是"四边形就是这张表面"），
   *    `unitsPerPx` 于是恰好是 1 —— 濒死阴影那种"渲染目标和四边形一样大"
   *    的烘焙场合什么都不用改。
   */
  targetSize: readonly [number, number];
  /** 网点节距（逻辑像素，设计尺寸） */
  dotPitch: number;
  /**
   * **网格线性放大几倍**（只有像素化档位 > 1）。
   *
   * 评审：「`FIELD_PIXEL_EXPONENT` 为正时，半调网格也跟着线性放大（不和像素化
   * 一样 `2^x` 放大）」。
   *
   * ⚠️ 它是**乘在节距上**的（`pitch = dotPitch × gridScale`），不是乘在格子
   *    坐标上 —— 后者会把整块网点挪位。默认 1（设计尺寸）。
   */
  gridScale: number;
  /**
   * **1 = 画成 1-bit 硬边**（二值化 + 吸像素格 + 情绪亮度走覆盖率）；
   * **0 = 柔边**（加像素化之前那条路，见 `renderScale.ts` 的
   * `FIELD_HALFTONE_BINARY`）。
   */
  binary: number;
  /** 网点排列角度（弧度）。15° / 45° 是印刷上的常规角度 */
  dotAngle: number;
  /**
   * **点的形状**：0 圆 / 1 方 / 2 菱形 / 3 十字 / 4 横线。
   *
   * 评审：「令点的形状可控」。形状只换"格子里的距离怎么算"（见着色器里的
   * `cellDistance`），所以加一种形状 = 加一个 if。
   */
  dotShape: number;
  /**
   * 边缘柔度（逻辑像素）。
   *
   * ⚠️ 它只在**柔边那一档**（`binary = 0`）用得上 —— 1-bit 那一档是
   * `step(dist, radius)`，没有柔边这回事。它是 `uDot` 那个 `vec4` 的第三个
   * 分量（pitch / angle / softness / gain）。
   */
  softness: number;
  /** 灰度对比度。>1 网点分得更开 */
  contrast: number;
  /** 纸色（没有点的地方） */
  paper: readonly [number, number, number];
  /** 墨色（点里） */
  ink: readonly [number, number, number];
  /**
   * 1 = 把网点当**镂空遮罩**用（alpha 跟覆盖率走，点之外透明）。
   * 默认 0 = 不透明印刷，整块画面都归这一级所有。
   */
  alphaFromInk: number;
  /** 情绪：亮度倍率。见 `mood.ts`。默认 1（不调） */
  brightness: number;
  /** 情绪：色调倍率。默认 `(1,1,1)`（不调） */
  tint: readonly [number, number, number];
}

/**
 * 半调那一级的 uniform 组。
 *
 * ⚠️ 单独抽出来（而不是塞在 `HalftoneFilter` 的构造函数里）是为了**能在 Node 里
 *    单测**：真建一个 `HalftoneFilter` 需要 canvas —— `GlProgram.from` 会去试
 *    片元精度（它会去造一块临时画布来试），无头 Node 里直接抛。
 *    而"`uTargetSize` 到底是多少"是个纯数据问题，值得单测（它决定网点能不能
 *    和像素网格对齐）。
 *
 * 规则：`targetSize` 不给就**等于 `resolution`**（"渲染目标和四边形一样大"，
 * 于是 `unitsPerPx = 1`）—— 濒死阴影那种烘焙场合一个字都不用改。
 * 给了就只认它（背景传的是那张 `1/2^n` 的 FBO）。
 */
export function createHalftoneUniforms(
  values: Partial<HalftoneUniformValues> = {},
): UniformGroup {
  const uniforms = new UniformGroup({
    uResolution: { value: new Float32Array([1280, 800]), type: 'vec2<f32>' },
    uQuad: { value: new Float32Array([0, 0, 1280, 800]), type: 'vec4<f32>' },
    // ⚠️ **不是** `uOutputTexture`：那个名字已经由顶点着色器声明过，片元这侧
    //    再声明一次会因为默认精度不同（highp / mediump）**链接失败**，
    //    整个滤镜变成纯黑。所以要用的这个由调用方传进来
    uTargetSize: { value: new Float32Array([1280, 800]), type: 'vec2<f32>' },
    // 网点节距：**粗一点**。印刷感的半调是靠"看得出颗粒"成立的，
    // 5~6 逻辑像素那种细网在 1280 宽的画布上会糊成一层灰雾
    uDot: { value: new Float32Array([11, Math.PI / 4, 1.1, 1]), type: 'vec4<f32>' },
    // 网格线性放大几倍（像素化档位 > 1，见 gridScale）
    uGridScale: { value: 1, type: 'f32' },
    // 1 = 硬边 1-bit，0 = 柔边（见 binary）
    uBinary: { value: 1, type: 'f32' },
    // ⚠️ **黑底白点**（acid design）。第一版做反了（奶黄纸 + 深点）。
    // 底必须是纯黑：只要底不是纯黑，白点的对比度就上不去，整块背景会发灰
    uPaper: { value: new Float32Array(rgb01(BASE.void)), type: 'vec3<f32>' },
    uInk: { value: new Float32Array(rgb01(LIGHT.halftone)), type: 'vec3<f32>' },
    uAlphaFromInk: { value: 0, type: 'f32' },
    // 情绪：默认中性。别的用处（濒死阴影烘焙）什么都不用改
    uMoodBrightness: { value: 1, type: 'f32' },
    // 点的形状（0 圆 / 1 方 / 2 菱形 / 3 十字 / 4 横线）
    uDotShape: { value: values.dotShape ?? 0, type: 'f32' },
    uMoodTint: { value: new Float32Array([1, 1, 1]), type: 'vec3<f32>' },
  });
  writeHalftoneUniforms(uniforms, {
    ...values,
    // 没明说就跟 resolution（`values.resolution` 也没有的话就是默认 1280×800）
    targetSize: values.targetSize ?? values.resolution,
  });
  return uniforms;
}

/** 把参数写进半调那一级的 uniform 组（只写给了的那些）。见 `createHalftoneUniforms` */
export function writeHalftoneUniforms(
  group: UniformGroup,
  values: Partial<HalftoneUniformValues>,
): void {
  const u = group.uniforms;
  if (values.resolution !== undefined) writeVec(u.uResolution, values.resolution);
  if (values.quad !== undefined) writeVec(u.uQuad, values.quad);
  // ⚠️ 只认**显式**给的 `targetSize`：`uResolution`（画布有多大）和
  //    `uTargetSize`（这一级往多大的表面上画）是两件事，后改前者不该动后者
  if (values.targetSize !== undefined) writeVec(u.uTargetSize, values.targetSize);
  if (values.paper !== undefined) writeVec(u.uPaper, values.paper);
  if (values.ink !== undefined) writeVec(u.uInk, values.ink);
  if (values.alphaFromInk !== undefined) u.uAlphaFromInk = values.alphaFromInk;
  if (values.brightness !== undefined) u.uMoodBrightness = values.brightness;
  if (values.tint !== undefined) writeVec(u.uMoodTint, values.tint);

  const dot = u.uDot as Float32Array;
  if (values.dotShape !== undefined) u.uDotShape = values.dotShape;
  if (values.gridScale !== undefined) u.uGridScale = values.gridScale;
  if (values.binary !== undefined) u.uBinary = values.binary;
  if (values.dotPitch !== undefined) dot[0] = values.dotPitch;
  if (values.dotAngle !== undefined) dot[1] = values.dotAngle;
  if (values.softness !== undefined) dot[2] = values.softness;
  if (values.contrast !== undefined) dot[3] = values.contrast;
}

/** 半调 filter —— 这一级才真的在加工输入（阶段一产出的灰阶） */
export class HalftoneFilter extends Filter {
  readonly halftoneUniforms: UniformGroup;

  constructor(values: Partial<HalftoneUniformValues> = {}) {
    const uniforms = createHalftoneUniforms(values);

    super({
      glProgram: quadProgram(HALFTONE_FRAGMENT, 'bg-halftone'),
      resources: { halftoneUniforms: uniforms },
    });

    this.halftoneUniforms = uniforms;
  }

  /** 同上：不叫 `apply`，那是 `Filter` 基类的管线方法 */
  setValues(values: Partial<HalftoneUniformValues>): void {
    writeHalftoneUniforms(this.halftoneUniforms, values);
  }
}

/** 把 `[r,g,b]` 之类的数组写进 uniform 的 Float32Array（原地，不重新分配） */
function writeVec(target: unknown, source: readonly number[]): void {
  const array = target as Float32Array;
  for (let i = 0; i < source.length && i < array.length; i++) {
    array[i] = source[i] ?? 0;
  }
}
